class ContactHub extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.types = Array.from(this.querySelectorAll("[data-contact-type]"));
    this.methods = Array.from(this.querySelectorAll("[data-contact-method]"));
    this.panels = Array.from(this.querySelectorAll("[data-contact-panel]"));
    this.faqGroups = Array.from(this.querySelectorAll("[data-faq-group]"));
    this.pointer = this.querySelector("[data-contact-pointer]");
    this.rail = this.querySelector("[data-contact-rail]");
    this.step2 = this.querySelector("[data-step2]");
    this.step3 = this.querySelector("[data-step3]");
    this.step3Title = this.querySelector("[data-step3-title]");
    this.activeType = null;
    this.activeMethod = null;

    this.types.forEach((btn) => {
      btn.addEventListener("click", () => this.selectType(btn.dataset.contactType));
    });

    this.methods.forEach((card) => {
      const actionBtn = card.querySelector("[data-method-action]");
      const icon = card.querySelector("[data-method-icon]");

      if (icon) {
        card.addEventListener("mouseenter", () => {
          if (this.classList.contains("is-step2-locked")) return;
          if (icon.dataset.iconActive) icon.src = icon.dataset.iconActive;
          icon.style.filter = "none";
        });
        card.addEventListener("mouseleave", () => {
          if (card.classList.contains("is-active")) {
            if (icon.dataset.iconActive) icon.src = icon.dataset.iconActive;
            icon.style.filter = "none";
            return;
          }
          if (icon.dataset.iconDefault) icon.src = icon.dataset.iconDefault;
          icon.style.filter = "";
        });
      }

      if (!actionBtn) return;
      actionBtn.addEventListener("click", (event) => {
        if (this.classList.contains("is-step2-locked") || card.hidden) {
          event.preventDefault();
          return;
        }

        const methodKey = card.dataset.contactMethod || "";
        const RETURN_URL =
          "https://feelingirl.com/apps/help-center#hc-what-is-feelingirls-return-policy";

        // Start a Return → always go to Help Center return policy.
        if (methodKey === "return") {
          event.preventDefault();
          window.location.href = RETURN_URL;
          return;
        }

        // Other external links (track / custom).
        if (actionBtn.tagName === "A" || actionBtn.hasAttribute("data-external-link")) {
          const href = actionBtn.getAttribute("href");
          if (!href) {
            event.preventDefault();
            return;
          }
          event.preventDefault();
          window.location.href = href;
          return;
        }

        event.preventDefault();
        this.selectMethod(methodKey);
      });
    });

    this.faqGroups.forEach((group) => {
      const toggle = group.querySelector("[data-faq-toggle]");
      if (!toggle) return;
      toggle.addEventListener("click", () => {
        const key = group.dataset.faqGroup;
        this.openFaqGroup(key, true);
      });
    });

    this.querySelector("[data-chat-reopen]")?.addEventListener("click", () => {
      this.openLiveChat();
    });

    // 初始化：默认选中第一个问题类型 + 第一个可见联系方式，不禁用 Step 2。
    // 原禁用逻辑（页面加载时灰显 Step 2）已停用，见下方 lockStep2()。
    // this.lockStep2();
    // this.filterMethods(null);
    // this.hideStep3();

    const firstType = this.types[0];
    if (firstType?.dataset.contactType) {
      this.selectType(firstType.dataset.contactType);
      const firstMethod = this.methods.find((card) => !card.hidden);
      if (firstMethod?.dataset.contactMethod) {
        this.selectMethod(firstMethod.dataset.contactMethod, { silent: true });
      }
    } else {
      this.unlockStep2();
    }

    requestAnimationFrame(() => this.updateRail());
  }

  selectType(typeKey) {
    this.activeType = typeKey;
    this.types.forEach((btn) => {
      const active = btn.dataset.contactType === typeKey;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-selected", active ? "true" : "false");
    });

    const active = this.types.find((btn) => btn.dataset.contactType === typeKey);
    const label = active?.dataset.typeLabel || typeKey;
    if (this.step3Title) this.step3Title.textContent = label;

    const ticketTypeField = this.querySelector("[data-ticket-type-field]");
    if (ticketTypeField) ticketTypeField.value = label;

    this.openFaqGroup(typeKey, false);
    this.filterMethods(typeKey);
    this.unlockStep2();
    this.clearMethod();
    this.hideStep3();
    this.updateRail();
  }

  parseHandles(value) {
    return String(value || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  filterMethods(typeKey) {
    this.methods.forEach((card) => {
      const key = card.dataset.contactMethod || "";
      let visibleFor = this.parseHandles(card.dataset.visibleFor);
      let hiddenFor = this.parseHandles(card.dataset.hiddenFor);

      // Built-in visibility rules (merge with schema settings).
      if (key === "return") {
        visibleFor = Array.from(new Set([...visibleFor, "returns-exchanges"]));
      }
      if (key === "faq" || key === "track") {
        hiddenFor = Array.from(
          new Set([
            ...hiddenFor,
            "returns-exchanges",
            "comments-complaints",
            "products-sizes",
            "account-points",
            "data-request",
          ])
        );
      }

      let show = true;
      if (!typeKey) {
        show = visibleFor.length === 0;
      } else {
        if (visibleFor.length) show = visibleFor.includes(typeKey);
        if (hiddenFor.includes(typeKey)) show = false;
      }

      card.hidden = !show;
      card.classList.toggle("is-method-hidden", !show);
    });
  }

  unlockStep2() {
    this.classList.remove("is-step2-locked");
    if (this.step2) this.step2.classList.remove("is-locked");
    const index = this.step2?.querySelector(".contact-hub__step-index");
    const head = this.step2?.querySelector(".contact-hub__step-head");
    index?.classList.remove("is-muted");
    head?.classList.remove("is-muted");
    this.querySelector(".contact-hub__methods")?.setAttribute("aria-disabled", "false");

    this.methods.forEach((card) => {
      if (card.hidden) return;
      const actionBtn = card.querySelector("[data-method-action]");
      if (!actionBtn) return;
      if (actionBtn.tagName === "BUTTON") actionBtn.disabled = false;
      else actionBtn.removeAttribute("tabindex");
    });
  }

  // 原 Step 2 禁用逻辑：未选问题类型前灰显联系方式。初始化已改为默认选中第一项，保留供参考。
  lockStep2() {
    this.classList.add("is-step2-locked");
    if (this.step2) this.step2.classList.add("is-locked");
    const index = this.step2?.querySelector(".contact-hub__step-index");
    const head = this.step2?.querySelector(".contact-hub__step-head");
    index?.classList.add("is-muted");
    head?.classList.add("is-muted");
    this.querySelector(".contact-hub__methods")?.setAttribute("aria-disabled", "true");
    this.filterMethods(null);

    this.methods.forEach((card) => {
      const actionBtn = card.querySelector("[data-method-action]");
      if (!actionBtn) return;
      if (actionBtn.tagName === "BUTTON") actionBtn.disabled = true;
      else actionBtn.setAttribute("tabindex", "-1");
    });
  }

  clearMethod() {
    this.activeMethod = null;
    this.closeLiveChat();
    this.methods.forEach((card) => {
      card.classList.remove("is-active");
      const btn = card.querySelector("[data-method-action]");
      if (btn) btn.classList.remove("is-primary");
      const icon = card.querySelector("[data-method-icon]");
      if (icon) {
        if (icon.dataset.iconDefault) icon.src = icon.dataset.iconDefault;
        icon.classList.remove("is-active-src");
      }
    });
    this.panels.forEach((panel) => {
      panel.hidden = true;
    });
  }

  selectMethod(methodKey, options = {}) {
    if (this.classList.contains("is-step2-locked") || !this.activeType) return;

    this.activeMethod = methodKey;
    this.setPanelReady(false);
    this.showStep3();

    this.methods.forEach((card) => {
      const active = card.dataset.contactMethod === methodKey;
      card.classList.toggle("is-active", active);
      const btn = card.querySelector("[data-method-action]");
      if (btn) btn.classList.toggle("is-primary", active);

      const icon = card.querySelector("[data-method-icon]");
      if (icon) {
        const nextSrc = active ? icon.dataset.iconActive : icon.dataset.iconDefault;
        if (nextSrc) icon.src = nextSrc;
        icon.classList.toggle("is-active-src", active);
        icon.style.filter = active ? "none" : "";
      }
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.contactPanel !== methodKey;
    });

    // Chat mode: hide type title (design shows bot only under step 3).
    const isChat = methodKey === "chat";
    this.classList.toggle("is-chat-open", isChat);
    if (this.step3) this.step3.classList.toggle("is-chat-mode", isChat);
    if (this.step3Title) {
      if (isChat) {
        this.step3Title.textContent = "";
      } else {
        const active = this.types.find((btn) => btn.dataset.contactType === this.activeType);
        this.step3Title.textContent = active?.dataset.typeLabel || this.activeType || "FAQ";
      }
    }

    if (isChat) {
      this.openLiveChat();
    } else {
      this.closeLiveChat();
      // FAQ / ticket content is sync — show pointer with the panel.
      requestAnimationFrame(() => this.setPanelReady(true));
    }

    this.updatePointer();
    this.updateRail();

    if (!options.silent) {
      this.scrollToPanel();
    }
  }

  setPanelReady(ready) {
    const wrap = this.querySelector(".contact-hub__panel-wrap");
    wrap?.classList.toggle("is-ready", Boolean(ready));
    this.step3?.classList.toggle("is-chat-ready", Boolean(ready) && this.activeMethod === "chat");
    if (ready) this.updatePointer();
  }

  showStep3() {
    if (!this.step3) return;
    this.step3.hidden = false;
  }

  hideStep3() {
    if (!this.step3) return;
    this.step3.hidden = true;
    this.step3.classList.remove("is-chat-mode", "is-chat-ready");
    this.classList.remove("is-chat-open");
    this.setPanelReady(false);
    this.closeLiveChat();
    this.panels.forEach((panel) => {
      panel.hidden = true;
    });
  }

  openFaqGroup(typeKey, fromToggle) {
    this.faqGroups.forEach((group) => {
      const match = group.dataset.faqGroup === typeKey;
      if (fromToggle) {
        group.classList.toggle("is-open", match ? !group.classList.contains("is-open") : false);
      } else {
        group.classList.toggle("is-open", match);
      }
    });
  }

  updateRail() {
    if (!this.rail) return;
    const steps = this.querySelector(".contact-hub__steps");
    const solid = this.rail.querySelector("[data-rail-solid]");
    const dot = this.rail.querySelector("[data-rail-dot]");
    const dash = this.rail.querySelector("[data-rail-dash]");
    const visibleIndexes = Array.from(this.querySelectorAll(".contact-hub__step-index")).filter((el) => {
      const step = el.closest(".contact-hub__step");
      return step && !step.hidden;
    });
    if (!steps || visibleIndexes.length < 2) {
      this.rail.style.height = "0px";
      return;
    }

    const stepsRect = steps.getBoundingClientRect();
    const first = visibleIndexes[0].getBoundingClientRect();
    const last = visibleIndexes[visibleIndexes.length - 1].getBoundingClientRect();
    const top = first.bottom - stepsRect.top;
    const bottom = last.top - stepsRect.top;
    const height = Math.max(0, bottom - top);
    const left = first.left + first.width / 2 - stepsRect.left - 1;

    this.rail.style.top = `${Math.round(top)}px`;
    this.rail.style.height = `${Math.round(height)}px`;
    this.rail.style.left = `${Math.round(left)}px`;

    // Always Cupshe-style light gray rail (never pure black).
    this.rail.classList.add("is-pending");
    this.rail.classList.remove("is-complete");
    // After selecting a question type (step1): lower segment becomes solid.
    this.rail.classList.toggle("is-step1-done", Boolean(this.activeType));

    if (!solid) return;

    const step1Done = Boolean(this.activeType);
    const dotSize = 8;

    // Pending: solid → mid-dot → dashed (Cupshe). After step1: one continuous solid.
    if (step1Done) {
      solid.style.height = `${Math.round(height)}px`;
      if (dot) dot.style.top = "0px";
      if (dash) {
        dash.style.top = "0px";
        dash.style.height = "0px";
      }
      return;
    }

    // Mid-dot centered between first and last visible circles.
    const solidHeight = Math.max(0, Math.round(height / 2 - dotSize / 2));
    solid.style.height = `${solidHeight}px`;
    if (dot) {
      dot.style.width = `${dotSize}px`;
      dot.style.height = `${dotSize}px`;
      dot.style.marginLeft = `${-dotSize / 2}px`;
      dot.style.top = `${solidHeight}px`;
    }
    if (dash) {
      const dashTop = solidHeight + dotSize;
      dash.style.top = `${dashTop}px`;
      dash.style.height = `${Math.max(0, height - dashTop)}px`;
    }
  }

  updatePointer() {
    if (!this.pointer || !this.step3 || this.step3.hidden) return;
    const wrap = this.querySelector(".contact-hub__panel-wrap");
    // Don't flash the tip before panel content is ready (esp. Live Chat).
    if (!wrap?.classList.contains("is-ready")) {
      this.pointer.classList.add("is-waiting");
      return;
    }
    this.pointer.classList.remove("is-waiting");

    const activeCard = this.methods.find(
      (card) => card.classList.contains("is-active") && !card.hidden
    );
    if (!activeCard) return;
    const methodsRow = this.querySelector(".contact-hub__methods");
    if (!methodsRow) return;
    const rowRect = methodsRow.getBoundingClientRect();
    const cardRect = activeCard.getBoundingClientRect();
    const left = cardRect.left - rowRect.left + cardRect.width / 2 - 11;
    this.pointer.style.left = `${Math.max(12, left)}px`;
  }

  openLiveChat() {
    document.documentElement.classList.add("contact-hub-chat-open");
    const mount = this.querySelector("[data-chat-mount]");
    const fallback = this.querySelector("[data-chat-fallback]");
    if (!mount) return;

    const markReady = () => {
      if (this.activeMethod !== "chat") return;
      this.setPanelReady(true);
      this.updateRail();
    };

    // Already embedded successfully — reveal immediately.
    if (this._chatEmbedded && mount.childElementCount > 0) {
      if (fallback) fallback.hidden = true;
      markReady();
      return;
    }

    const showFallback = () => {
      if (fallback) fallback.hidden = false;
      markReady();
    };

    const watchMount = () => {
      if (mount.childElementCount > 0) {
        markReady();
        return;
      }
      const observer = new MutationObserver(() => {
        if (mount.childElementCount > 0) {
          observer.disconnect();
          // Let Zendesk paint one frame before showing the tip.
          requestAnimationFrame(() => markReady());
        }
      });
      observer.observe(mount, { childList: true, subtree: true });
      // Safety timeout so tip doesn't stay hidden forever.
      window.setTimeout(() => {
        observer.disconnect();
        markReady();
      }, 2500);
    };

    const renderEmbedded = () => {
      try {
        if (typeof window.zE !== "function") {
          showFallback();
          return;
        }

        window.zE("messenger", "render", {
          mode: "embedded",
          widget: {
            targetElement: "#contact-hub-chat-mount",
          },
        });
        this._chatEmbedded = true;
        if (fallback) fallback.hidden = true;
        watchMount();
      } catch (error) {
        showFallback();
      }
    };

    this.waitForMountReady(mount, () => {
      this.whenZendeskReady(renderEmbedded, showFallback);
    });
  }

  closeLiveChat() {
    document.documentElement.classList.remove("contact-hub-chat-open");
  }

  waitForMountReady(mount, onReady) {
    let tries = 0;
    const maxTries = 60;
    const tick = () => {
      const ready = mount.clientHeight >= 200 && mount.offsetParent !== null;
      if (ready || tries >= maxTries) {
        onReady();
        return;
      }
      tries += 1;
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  }

  whenZendeskReady(onReady, onFail) {
    const start = Date.now();
    const maxWait = 8000;
    let done = false;

    const finish = (fn) => {
      if (done || typeof fn !== "function") return;
      done = true;
      fn();
    };

    const tryReady = () => {
      if (typeof window.zE === "function") {
        let invoked = false;
        const run = () => {
          if (invoked) return;
          invoked = true;
          finish(onReady);
        };
        try {
          window.zE("messenger", "on", "ready", run);
        } catch (error) {
          /* ignore */
        }
        // Fallback if ready already fired before we subscribed.
        window.setTimeout(run, 500);
        return;
      }
      if (Date.now() - start > maxWait) {
        finish(onFail);
        return;
      }
      window.setTimeout(tryReady, 200);
    };

    tryReady();
  }

  scrollToPanel() {
    const wrap = this.querySelector(".contact-hub__panel-wrap");
    if (!wrap) return;
    wrap.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }
}

if (!customElements.get("contact-hub")) {
  customElements.define("contact-hub", ContactHub);
}

window.addEventListener("resize", () => {
  document.querySelectorAll("contact-hub").forEach((el) => {
    if (typeof el.updatePointer === "function") el.updatePointer();
    if (typeof el.updateRail === "function") el.updateRail();
  });
});
